"use client";

import type React from "react";
import { createContext, useCallback, useContext, useState } from "react";
import { createLogAdapter } from "@/lib/logger/console-adapter";

interface UserInfo {
	principal: string;
	username: string | null;
	image: string | null;
	timestamp: number; // 缓存时间戳
}

interface UserInfoContextType {
	getUserInfo: (userid: string) => Promise<UserInfo>;
	getCachedUserInfo: (userid: string) => UserInfo | null;
}

const UserInfoContext = createContext<UserInfoContextType | undefined>(
	undefined,
);

const CACHE_DURATION = 5 * 60 * 1000; // 5分钟缓存
const API_BASE_URL = "https://api.odin.fun/v1";
const uiLog = createLogAdapter("contexts/UserInfoContext.tsx");

export function UserInfoProvider({ children }: { children: React.ReactNode }) {
	const [cache, setCache] = useState<Map<string, UserInfo>>(new Map());

	// 检查缓存是否过期
	const isCacheValid = useCallback((userInfo: UserInfo): boolean => {
		return Date.now() - userInfo.timestamp < CACHE_DURATION;
	}, []);

	// 从缓存获取用户信息（不触发API请求）
	const getCachedUserInfo = useCallback(
		(userid: string): UserInfo | null => {
			const cached = cache.get(userid);
			if (cached && isCacheValid(cached)) {
				return cached;
			}
			return null;
		},
		[cache, isCacheValid],
	);

	// 获取用户信息（带缓存）
	const getUserInfo = useCallback(
		async (userid: string): Promise<UserInfo> => {
			// 检查缓存
			const cached = cache.get(userid);
			if (cached && isCacheValid(cached)) {
				return cached;
			}

			// 发起API请求
			try {
				const response = await fetch(`${API_BASE_URL}/user/${userid}`);

				if (!response.ok) {
					throw new Error(`API request failed: ${response.status}`);
				}

				const data = await response.json();

				const userInfo: UserInfo = {
					principal: data.principal || userid,
					username: data.username || null,
					image: data.image || null,
					timestamp: Date.now(),
				};

				// 更新缓存
				setCache((prev) => {
					const newCache = new Map(prev);
					newCache.set(userid, userInfo);
					return newCache;
				});

				return userInfo;
			} catch (error) {
				uiLog.error(`Failed to fetch user info for ${userid}:`, error);

				// 失败时创建降级数据（无username）
				const fallbackInfo: UserInfo = {
					principal: userid,
					username: null,
					image: null,
					timestamp: Date.now(),
				};

				// 缓存失败结果（避免重复请求）
				setCache((prev) => {
					const newCache = new Map(prev);
					newCache.set(userid, fallbackInfo);
					return newCache;
				});

				return fallbackInfo;
			}
		},
		[cache, isCacheValid],
	);

	return (
		<UserInfoContext.Provider value={{ getUserInfo, getCachedUserInfo }}>
			{children}
		</UserInfoContext.Provider>
	);
}

export function useUserInfo() {
	const context = useContext(UserInfoContext);
	if (context === undefined) {
		throw new Error("useUserInfo must be used within a UserInfoProvider");
	}
	return context;
}
